/**
 * ASCII Art Generator - 默认渲染设置
 *
 * 定义所有参数的默认值，与ASC11保持一致
 */

import type {
  FullRenderSettings,
  StyleType,
  CharsetType,
  BrailleVariant,
  HalftoneShape,
  DitherType,
  ColorMode,
  RetroDuotone,
  TerminalCharset,
  OverlayPreset,
  EffectDirection,
  MouseInteractionMode,
  OutputAspect,
} from './types'

/** 默认渲染设置 */
export const DEFAULT_SETTINGS: FullRenderSettings = {
  // 基础参数
  style: 'classic' as StyleType,
  fontSize: 10,
  charSpacing: 1,
  spacing: 1,
  contrast: 1,
  brightness: 0,
  opacity: 1,
  invert: false,
  backgroundColor: '#000000',

  // 字符集
  charset: 'standard' as CharsetType,
  customCharset: ' .:-=+*#%@',
  brailleVariant: 'standard' as BrailleVariant,
  terminalCharset: 'binary' as TerminalCharset,

  // 颜色
  colorMode: 'grayscale' as ColorMode,
  customColor: '#00ff99',

  // 半调
  halftoneShape: 'circle' as HalftoneShape,
  halftoneSize: 1,
  halftoneRotation: 0,

  // 抖动
  ditherType: 'floyd-steinberg' as DitherType,
  ditherStrength: 0.8,

  // 复古
  retroDuotone: 'amber-classic' as RetroDuotone,
  retroNoise: 0.45,

  // 线条
  lineLength: 1,
  lineWidth: 1,
  lineThickness: 1.6,
  lineRotation: 0,

  // 粒子
  particleDensity: 0.5,
  particleChar: '*',

  // Claude
  claudeDensity: 0.7,

  // 动画类型
  animationType: 'wave' as any,

  // Dither动画
  ditherAnimationEnabled: true,
  ditherMouseInteractionEnabled: true,
  ditherMouseRadius: 0.3,
  mouseInteractionMode: 'attract' as MouseInteractionMode,
  ditherWaveAmplitude: 0.3,
  ditherWaveFrequency: 3,
  ditherWaveSpeed: 0.05,
  ditherWaveColor: '#808080',
  ditherColorNum: 4,

  // Dither2动画
  dither2WaveColor: '#666666',
  dither2AnimationEnabled: true,
  dither2MouseInteractionEnabled: true,
  dither2MouseRadius: 1,
  dither2ColorNum: 4,
  dither2PixelSize: 2,
  dither2WaveAmplitude: 0.3,
  dither2WaveFrequency: 3,
  dither2WaveSpeed: 0.05,

  // 叠加效果
  overlayPreset: 'noise' as OverlayPreset,
  overlayStrength: 0.45,
  noiseScale: 24,
  noiseSpeed: 1,
  noiseDirection: 'right' as EffectDirection,
  glitchDirection: 'right' as EffectDirection,
  crtDirection: 'down' as EffectDirection,
  matrixDirection: 'down' as EffectDirection,
  matrixScale: 18,
  matrixSpeed: 1,
  intervalSpacing: 12,
  intervalSpeed: 1,
  intervalWidth: 2,
  intervalDirection: 'down' as EffectDirection,
  beamDirection: 'right' as EffectDirection,

  // 视觉效果
  vignette: 0,
  borderGlow: 0,
  bgDither: 0,
  inverseDither: 0,

  // 输出
  outputAspect: 'source' as OutputAspect,
  renderFont: '"Helvetica Neue", Helvetica, Arial, sans-serif',

  // 鼠标交互通用
  hoverStrength: 24,
  mouseAreaSize: 180,
  mouseSpread: 1,
}

// ============================================
// 参数范围定义
// ============================================

/** 参数范围配置 */
export const PARAM_RANGES = {
  fontSize: { min: 6, max: 20, step: 1, default: 10 },
  charSpacing: { min: 0.7, max: 2, step: 0.05, default: 1 },
  spacing: { min: 1, max: 3, step: 0.1, default: 1 },
  contrast: { min: 0, max: 2, step: 0.05, default: 1 },
  brightness: { min: -1, max: 1, step: 0.05, default: 0 },
  opacity: { min: 0, max: 1, step: 0.05, default: 1 },
  vignette: { min: 0, max: 1, step: 0.05, default: 0 },
  borderGlow: { min: 0, max: 1, step: 0.05, default: 0 },
  bgDither: { min: 0, max: 1, step: 0.05, default: 0 },
  inverseDither: { min: 0, max: 1, step: 0.05, default: 0 },
  ditherStrength: { min: 0, max: 1, step: 0.05, default: 0.8 },
  halftoneSize: { min: 0.4, max: 2.2, step: 0.1, default: 1 },
  halftoneRotation: { min: -180, max: 180, step: 5, default: 0 },
  retroNoise: { min: 0, max: 1, step: 0.05, default: 0.45 },
  particleDensity: { min: 0, max: 1, step: 0.05, default: 0.5 },
  claudeDensity: { min: 0, max: 1, step: 0.05, default: 0.7 },
  hoverStrength: { min: 12, max: 44, step: 1, default: 24 },
  mouseAreaSize: { min: 120, max: 320, step: 10, default: 180 },
  mouseSpread: { min: 0.6, max: 1.8, step: 0.05, default: 1 },
  ditherWaveAmplitude: { min: 0, max: 1, step: 0.05, default: 0.3 },
  ditherWaveFrequency: { min: 0, max: 10, step: 0.5, default: 3 },
  ditherWaveSpeed: { min: 0, max: 1, step: 0.01, default: 0.05 },
  ditherMouseRadius: { min: 0.1, max: 1.5, step: 0.05, default: 0.3 },
  ditherColorNum: { min: 2, max: 8, step: 1, default: 4 },
  overlayStrength: { min: 0, max: 1, step: 0.05, default: 0.45 },
  noiseScale: { min: 1, max: 100, step: 1, default: 24 },
  noiseSpeed: { min: 0, max: 5, step: 0.1, default: 1 },
  matrixScale: { min: 8, max: 32, step: 1, default: 18 },
  matrixSpeed: { min: 0, max: 5, step: 0.1, default: 1 },
  intervalSpacing: { min: 4, max: 32, step: 1, default: 12 },
  intervalSpeed: { min: 0, max: 5, step: 0.1, default: 1 },
  intervalWidth: { min: 1, max: 8, step: 1, default: 2 },
  lineLength: { min: 0.5, max: 3, step: 0.1, default: 1 },
  lineWidth: { min: 0.5, max: 3, step: 0.1, default: 1 },
  lineThickness: { min: 0.5, max: 5, step: 0.1, default: 1.6 },
  lineRotation: { min: -180, max: 180, step: 5, default: 0 },
} as const

// ============================================
// 参数验证工具
// ============================================

/** 参数范围类型 */
export interface ParamRange {
  min: number
  max: number
  step: number
  default: number
}

/**
 * 将参数值限制在有效范围内
 */
export function clampParam(value: number, range: ParamRange): number {
  // 先限制到 min/max 范围
  let clamped = Math.max(range.min, Math.min(range.max, value))
  // 按 step 取整
  const steps = Math.round((clamped - range.min) / range.step)
  clamped = range.min + steps * range.step
  // 再次限制（防止步进后超出）
  return Math.max(range.min, Math.min(range.max, clamped))
}

/**
 * 验证参数是否在有效范围内
 */
export function validateParam(value: number, range: ParamRange): { valid: boolean; message?: string } {
  if (value < range.min) {
    return { valid: false, message: `参数值 ${value} 小于最小值 ${range.min}` }
  }
  if (value > range.max) {
    return { valid: false, message: `参数值 ${value} 大于最大值 ${range.max}` }
  }
  return { valid: true }
}

/**
 * 批量验证并修正参数
 */
export function validateAndClampSettings(settings: Partial<FullRenderSettings>): Partial<FullRenderSettings> {
  const result: Record<string, unknown> = { ...settings }

  for (const [key, range] of Object.entries(PARAM_RANGES)) {
    if (key in result && typeof result[key] === 'number') {
      result[key] = clampParam(result[key] as number, range as ParamRange)
    }
  }

  return result as Partial<FullRenderSettings>
}

// ============================================
// 渲染字体列表
// ============================================

export const RENDER_FONTS = [
  '"Helvetica Neue", Helvetica, Arial, sans-serif',
  '"Inter", "Helvetica Neue", Helvetica, Arial, sans-serif',
  '"Poppins", "Helvetica Neue", Helvetica, Arial, sans-serif',
  '"Space Grotesk", "Helvetica Neue", Helvetica, Arial, sans-serif',
  '"VT323", "Arial", sans-serif',
] as const

// ============================================
// 背景颜色预设
// ============================================

export const BACKGROUND_COLORS = [
  '#000000',
  '#030712',
  '#0b0f19',
  '#111827',
  '#1f2937',
  '#1a1a1a',
] as const

// ============================================
// 强调颜色预设
// ============================================

export const ACCENT_COLORS = [
  '#00ff99',
  '#22d3ee',
  '#f97316',
  '#eab308',
  '#f43f5e',
  '#a78bfa',
] as const